import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Briefcase,
  UserCheck,
  ScanSearch,
  Compass,
  Layers3,
  Microscope,
  HeartPulse,
  GraduationCap,
  School,
  Landmark,
  Handshake,
  ShieldCheck,
  Wrench,
  ClipboardList,
  Eye,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import WebCenterStackDiagram from '@/components/diagrams/WebCenterStackDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Briefcase,
    title: '25 years across the Oracle ecosystem',
    detail:
      'Andrew Blackman has spent 25 years in the Oracle ecosystem and 20+ years specifically inside WebCenter Content (UCM/Stellent), Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA Suite, WebLogic (11g, 12c, 14c), and ADF customizations.',
  },
  {
    icon: ScanSearch,
    title: 'We read implementations directly',
    detail:
      'Forms Recognition projects, custom WCC components, SOA composites, BPEL processes, WebLogic domains, ADF customizations, FIPSA configurations, EBS / Fusion integration, and AME workflows — read directly off the running system, without requiring the original implementer or extensive customer documentation.',
  },
  {
    icon: Compass,
    title: 'Two delivery modes, four engagement tiers',
    detail:
      'Standard fixed-fee consulting for upgrades, OCI lift-and-shift, and modernization projects. Forward Deployed Engineering for complex or undocumented implementations that need a senior engineer hands-on rather than a delivery team handed a runbook.',
  },
  {
    icon: ShieldCheck,
    title: 'Oracle Reference Architecture published',
    detail:
      'EZ Cloud is published on the Oracle Reference Architecture for OCI and featured in Oracle\'s own technical case studies. The consulting practice runs alongside the platform — same founder, same WebCenter depth, applied to whichever forward path each customer chooses.',
  },
];

const DELIVERY_MODES = [
  {
    icon: ClipboardList,
    badge: 'Mode 1',
    title: 'Standard WebCenter Consulting',
    blurb:
      'Fixed-fee engagements with defined scope. Runbook-driven, predictable, suitable for the well-traveled WebCenter forward paths.',
    bestFor: 'Best for',
    bestForItems: [
      'WebCenter 12c → 14c upgrade engagements',
      'OCI Marketplace lift-and-shift of an existing 12c implementation',
      'SOA Suite migrations and WebLogic 14c upgrades',
      'File store migration to OCI Object Storage',
      'AME workflow review and 14c-compatibility validation',
      'Customers with clean documentation, available SMEs, and a defined target state',
    ],
  },
  {
    icon: UserCheck,
    badge: 'Mode 2',
    title: 'Forward Deployed Engineering',
    blurb:
      'Andrew works directly and continuously with your team through the delivery. Suitable for complex, undocumented, or reverse-engineering-heavy work where the standard runbook does not apply.',
    bestFor: 'Best for',
    bestForItems: [
      'Forms Recognition archeology — projects with limited remaining documentation or original implementer access',
      'ADF non-PO coding form scenarios where "every small change is a development project"',
      'Custom WCC component reverse-engineering without source documentation',
      'Undocumented legacy implementations where the team needs to understand what they have before deciding what to do',
      'Complex or undocumented implementations that need a senior engineer hands-on, not a delivery team handed a runbook',
      'Hybrid programs that blend assessment, advisory, and selective delivery under one engagement',
    ],
  },
];

const ENGAGEMENT_TIERS = [
  {
    tier: 'Tier 1',
    label: 'Upgrade Assessment',
    mode: 'Standard Consulting',
    title: 'Fixed-fee inventory and forward plan',
    body:
      'Andrew reads your existing WebCenter implementation directly and delivers a structured forward plan — upgrade, lift-and-shift, modernize, or hybrid. Surfaces every customization, deprecated-feature dependency, integration, and migration consideration. The plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    tier: 'Tier 2',
    label: 'Architecture Review',
    mode: 'Standard Consulting',
    title: 'Deeper read of the running implementation',
    body:
      'A one-week fixed-fee read of your running WebCenter — Forms Recognition rules, SOA composites, WebLogic configuration, FIPSA accelerators, ADF customizations, EBS / Fusion integration, AME routing. Output is a structured inventory that becomes the working document for any 14c upgrade, OCI lift-and-shift, or modernization decision.',
  },
  {
    tier: 'Tier 3',
    label: 'Strategic Advisory Retainer',
    mode: 'Standard Consulting',
    title: 'Founder-level guidance through the decision window',
    body:
      'Monthly retainer with Andrew, sized for the customer working through a 6-12 month decision with internal stakeholders, procurement, and Oracle. Strategic guidance on architecture choices, customization tradeoffs, and 14c-specific considerations. Sits alongside any internal team or incumbent Oracle partner.',
  },
  {
    tier: 'Tier 4',
    label: 'Project Delivery',
    mode: 'Standard or Forward Deployed',
    title: 'Specific outcomes, fixed scope or embedded engagement',
    body:
      'Forms Recognition rules migration audits, FIPSA-specific upgrade assessments, OCI architecture and security setup, WebLogic 14c migration, ADF 14c compatibility review, file store to OCI Object Storage migration. Delivered as either a Standard fixed-fee project or as a Forward Deployed Engineering engagement when the scope calls for Andrew personally on the work.',
  },
];

const READ_DIRECTLY = [
  {
    icon: ScanSearch,
    title: 'WebCenter Forms Recognition projects',
    detail:
      'Templates, definitions, learned data, custom scripts, vendor-specific extraction rules. Read directly from the running WFR project files and database — no need for the original implementer to walk us through.',
  },
  {
    icon: Layers3,
    title: 'Custom WCC components',
    detail:
      'iDoc Script customizations, custom Java services, service handlers, security models, custom metadata. Read from the component .zip + hda definitions + actual deployment, including components built years ago by people no longer at the customer.',
  },
  {
    icon: Wrench,
    title: 'SOA composites and BPEL processes',
    detail:
      'AP invoice routing composites, vendor approval BPELs, exception handler flows. Read from the SCA archives and Mediator routing rules. Surfaces upgrade considerations specific to 12c → 14c composite migration.',
  },
  {
    icon: Microscope,
    title: 'WebLogic domains and ADF customizations',
    detail:
      'Cluster configuration, JDBC pools, security realms, ADF coding form customizations including the well-known ADF non-PO coding form scenarios. Read from config.xml + ADF library archives + custom skin definitions.',
  },
  {
    icon: ShieldCheck,
    title: 'FIPSA configurations and EBS / Fusion integration',
    detail:
      'Financial Image Processing Solution Accelerator configurations, AME workflow routing, EBS Open Interface and Fusion ERP REST integration patterns. Read end-to-end across the imaging-to-posting path.',
  },
  {
    icon: Eye,
    title: 'Implementations without source documentation',
    detail:
      'Customers often inherit WebCenter from prior teams, prior consultants, or acquisitions. We work without requiring full source documentation or SME interviews — the running implementation is the source of truth, and we read it.',
  },
];

const FORWARD_PATHS = [
  {
    label: 'Path A',
    title: 'Lift-and-shift to OCI Marketplace',
    body:
      'WebCenter 12c moves to OCI on the published Oracle Reference Architecture. Infrastructure modernizes; the WebCenter version and customizations carry forward as-is. Useful when the team wants OCI infrastructure benefits without taking on a 14c upgrade in the same window.',
    link: '/solutions/webcenter-oci-marketplace-vs-modernize',
  },
  {
    label: 'Path A+',
    title: '12c → 14c upgrade',
    body:
      'Out-of-place domain directory upgrade using Oracle\'s Upgrade Assistant. New 14c domain created alongside the 12c domain; configuration, custom components, Forms Recognition projects, SOA composites, and ADF customizations migrated and validated. Buys approximately 8 years of supported runway.',
    link: '/solutions/webcenter-14c-upgrade',
  },
  {
    label: 'Path B',
    title: 'Modernize off WebCenter',
    body:
      'Replace the AP automation layer with an AI-driven platform; Oracle EBS or Fusion stays as the system of record. Customizations are translated forward into the modern platform; some simplify away. Single decision made once, no recurring lifecycle window.',
    link: '/solutions/modernize-oracle-webcenter',
  },
  {
    label: 'Path B+',
    title: 'Hybrid',
    body:
      'A phased path that combines elements of A/A+ with B. Lift-and-shift now to clear the December 2026 cliff, modernize selected workloads over a longer window, retire WebCenter at a chosen pace. Suitable when stakeholder consensus or budget cycles require staging.',
    link: '/solutions/modernize-oracle-webcenter',
  },
];

const VERTICALS = [
  {
    icon: HeartPulse,
    name: 'Healthcare',
    body:
      'Oracle Fusion on OCI with WebCenter heritage in the AP path. Multi-fund coding, charity-care exceptions, and period-close edge cases that need to be respected end-to-end.',
    link: '/industries/healthcare',
  },
  {
    icon: GraduationCap,
    name: 'Higher Education',
    body:
      'Banner-anchored finance shops and Oracle EBS / Fusion campuses with WebCenter AP automation. Procurement, grants, and capital-project posting paths.',
    link: '/industries/education',
  },
  {
    icon: School,
    name: 'K-12',
    body:
      'Oracle EBS K-12 districts with WebCenter Imaging and Forms Recognition in the AP workflow. Board-approval cycles, state reporting, and long implementer tenure shape every modernization decision.',
    link: '/industries/education',
  },
  {
    icon: Landmark,
    name: 'Government',
    body:
      'Federal, state, and municipal Oracle deployments with WebCenter Content as the system-of-record archive. Records retention, FOIA workflows, and security controls layered into the AP path.',
    link: '/industries/professional-services',
  },
];

const ENGAGEMENT_COMPARISON = [
  {
    dimension: 'Pricing model',
    standard: 'Fixed-fee on defined scope',
    fde: 'Daily or weekly rate, with Andrew personally on the engagement',
  },
  {
    dimension: 'Scope flexibility',
    standard: 'Locked at SOW; change requests handled discretely',
    fde: 'Adaptive within engagement boundaries — exploration is part of the value',
  },
  {
    dimension: 'Who does the work',
    standard: 'Andrew leads; delivery support as needed',
    fde: 'Andrew personally on the work for the engagement duration',
  },
  {
    dimension: 'IP transfer',
    standard: 'Deliverables and documentation transferred at closeout',
    fde: 'Working artifacts, decision rationale, and code transferred continuously',
  },
  {
    dimension: 'Typical engagement length',
    standard: '2-12 weeks per project',
    fde: '8-26 weeks, sometimes longer for staged modernization programs',
  },
  {
    dimension: 'Best for',
    standard: 'Known forward path, clean documentation, defined target state',
    fde: 'Complex or undocumented implementations, archeology work, reverse-engineering-heavy delivery',
  },
];

const CONTROL_ITEMS = [
  {
    icon: ShieldCheck,
    title: 'Oracle support relationship',
    detail:
      'Your Oracle Premier or Extended Support contract stays with Oracle. We work inside what your Oracle support entitlement permits and surface Service Request paths where they apply.',
  },
  {
    icon: Layers3,
    title: 'ERP system of record',
    detail:
      'Oracle EBS or Fusion remains the financial system of record across every engagement. Invoices post against the GL; AP automation feeds the ERP; the ERP does not change shape because of how we engage.',
  },
  {
    icon: ClipboardList,
    title: 'Audit trail and controls',
    detail:
      'Existing audit trails and segregation-of-duties controls are preserved end-to-end. Any architecture change we recommend is presented with the controls implication called out explicitly.',
  },
  {
    icon: Handshake,
    title: 'Existing partner relationships',
    detail:
      'We work alongside, not in place of, existing Oracle partner relationships where the customer wants both lanes. Tier 3 Strategic Advisory is specifically built for this — founder-level input that complements an incumbent delivery partner.',
  },
];

const FAQS = [
  {
    q: 'What makes EZ Cloud different from other Oracle partners on WebCenter work?',
    a: 'EZ Cloud is a founder-led practice where Andrew Blackman personally reads every WebCenter implementation we engage on. He brings 20+ years specifically inside WebCenter Content, Imaging, Capture, Forms Recognition, SOA Suite, WebLogic, and ADF. That depth lets us assess implementations without requiring the original implementer or extensive customer documentation. The Forward Deployed Engineering option exists specifically because some scenarios require a senior engineer embedded in the work rather than a delivery team handed a runbook.',
  },
  {
    q: 'Standard Consulting vs Forward Deployed Engineering — how do we choose?',
    a: 'Standard Consulting fits when the forward path is well-understood, your team has documentation and available SMEs, and the work fits a fixed-fee scope. Forward Deployed Engineering fits when you are reverse-engineering an implementation with limited documentation, working through a complex or undocumented implementation, or running a hybrid program that blends assessment, advisory, and delivery under one engagement. We discuss this on the Decision Call — most customers do not need to decide before the call.',
  },
  {
    q: 'How is pricing structured?',
    a: 'Standard Consulting engagements are fixed-fee against a defined scope, while Forward Deployed Engineering is a daily or weekly rate with Andrew personally on the engagement. Tier 1 Upgrade Assessments are sized to the implementation footprint. Tier 2 Architecture Reviews run roughly a calendar week. Tier 3 Strategic Advisory Retainers are monthly. Specific pricing is established per situation on the Decision Call once the implementation footprint and decision window are understood.',
  },
  {
    q: 'Can EZ Cloud work alongside our existing Oracle partner?',
    a: 'Yes — the Tier 3 Strategic Advisory Retainer is specifically structured for this, providing founder-level input that complements an incumbent delivery partner. Tier 1 and Tier 2 assessments are also commonly engaged this way: an independent read of the implementation, with the resulting plan informing both internal stakeholders and the incumbent delivery partner. The deliverable is yours regardless of which partner executes.',
  },
  {
    q: 'Do you do fixed-fee engagements?',
    a: 'Yes — Tier 1 Upgrade Assessment, Tier 2 Architecture Review, and most Tier 4 Project Delivery engagements are fixed-fee against a defined scope. Tier 3 Strategic Advisory is a monthly retainer. Forward Deployed Engineering engagements are rate-based by design because the scope is adaptive; we agree engagement boundaries and a change-control mechanism upfront so the customer keeps visibility on burn.',
  },
  {
    q: 'What is the typical engagement timeline?',
    a: 'Tier 1 Upgrade Assessments typically run 2-4 weeks, and Tier 2 Architecture Reviews run a calendar week of read time plus 1-2 weeks for the structured deliverable. Tier 3 Strategic Advisory is open-ended, sized for the customer\'s 6-12 month decision window. Tier 4 Project Delivery engagements run 2-12 weeks for Standard scope; Forward Deployed Engineering engagements typically run 8-26 weeks, sometimes longer for staged programs.',
  },
  {
    q: 'Can you read our implementation without source documentation?',
    a: 'Yes — reading implementations without source documentation is a primary differentiator. We read Forms Recognition project files, custom WCC component .zip and .hda definitions, SOA SCA archives, WebLogic config.xml, ADF library archives, FIPSA configurations, AME workflow definitions, and EBS or Fusion integration patterns directly from the running implementation. Source documentation and SME interviews accelerate the read when they exist, but they are not a prerequisite for the engagement to start.',
  },
  {
    q: 'How does the Decision Call work?',
    a: 'The Decision Call is a 30-minute conversation with Andrew where you describe what you have on the floor — WebCenter version, AP volume, stack components, current support window, who else is in the room on the decision. Andrew walks through the relevant forward paths and engagement options in the context of your specific implementation. The call is not a sales pitch; the goal is to compress the decision-making timeline and give you a working frame to take back to your team.',
  },
];

const WebCenterModernizationServices = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter Modernization Services — Founder-Led Consulting for the December 2026 Decision | EZ Cloud',
    description:
      'Oracle WebCenter modernization consulting led by Andrew Blackman, 25-year Oracle WebCenter specialist. Four engagement tiers covering assessment, architecture review, strategic advisory, and project delivery. Standard consulting for 14c upgrades, OCI Marketplace lift-and-shift, and modernization off WebCenter. Forward Deployed Engineering option for complex, undocumented, or deeply customized implementations.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-modernization-services',
    },
    about: [
      'Oracle WebCenter Consulting',
      'Oracle WebCenter Implementation Partner',
      'Oracle WebCenter Modernization Services',
      'Forward Deployed Engineering',
      'Oracle Consulting',
      'Accounts Payable Automation',
    ].map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter Modernization Services — Founder-Led Consulting for the December 2026 Decision | EZ Cloud"
        description="Oracle WebCenter modernization consulting led by Andrew Blackman, 25-year Oracle WebCenter specialist. Four engagement tiers covering assessment, architecture review, strategic advisory, and project delivery. Standard consulting for 14c upgrades, OCI Marketplace lift-and-shift, and modernization off WebCenter. Forward Deployed Engineering option for complex, undocumented, or deeply customized implementations."
        canonical="https://ezcloud.ai/solutions/webcenter-modernization-services"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter Modernization Services',
            url: 'https://ezcloud.ai/solutions/webcenter-modernization-services',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Briefcase className="w-4 h-4" aria-hidden />
              WebCenter modernization consulting · Founder-led
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter modernization services
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Standard consulting and Forward Deployed Engineering for the December 2026 window.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Founder-led consulting for the December 2026 decision window. Standard engagements for
              upgrades, OCI lift-and-shift, and modernization. Forward Deployed Engineering for
              complex or undocumented implementations.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('modernization-services-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              A founder-led Oracle WebCenter consulting practice
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">EZ Cloud</strong> is a founder-led Oracle
                WebCenter consulting practice. Andrew Blackman, the founder, has spent 25 years
                across the Oracle ecosystem and 20+ years specifically inside Oracle WebCenter —
                Content (UCM/Stellent), Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA
                Suite, WebLogic Server (11g, 12c, 14c), ADF customizations, BPM, and Managed
                Attachments for E-Business Suite.
              </p>
              <p>
                The practice operates in <strong className="text-gray-900">two delivery modes</strong>.
                Standard Consulting handles fixed-fee engagements with defined scope — 14c upgrades,
                OCI Marketplace lift-and-shift, SOA Suite migrations, WebLogic upgrades, file store
                migration to OCI Object Storage. Forward Deployed Engineering is an
                Andrew-led embedded engagement for complex, undocumented, or reverse-engineering-heavy
                work where the standard runbook does not apply: WFR archeology, ADF non-PO coding form
                scenarios, custom WCC component reverse-engineering, undocumented legacy implementations.
              </p>
              <p>
                Engagements are organised into{' '}
                <strong className="text-gray-900">four tiers</strong> — Upgrade Assessment,
                Architecture Review, Strategic Advisory Retainer, and Project Delivery — and work
                alongside any existing Oracle partner relationship the customer wants to keep. EZ
                Cloud is published on the{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:text-accent transition-colors"
                >
                  Oracle Reference Architecture for OCI
                </a>{' '}
                and featured in{' '}
                <a
                  href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:text-accent transition-colors"
                >
                  Oracle&rsquo;s own technical case studies
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The practice at a glance
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What makes the WebCenter consulting practice work
            </h2>
            <div className="max-w-5xl mx-auto mb-12">
              <WebCenterStackDiagram />
            </div>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same founder-level WebCenter expertise that powers EZ Cloud&rsquo;s platform anchors
              the consulting work. Same Andrew, same depth, applied to whichever forward path each
              customer chooses.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Two delivery modes */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How we engage
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Two delivery modes
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Most customers fit Standard Consulting cleanly. A meaningful minority — typically
              complex, undocumented, archeology, or deeply customized implementations — need the
              Forward Deployed Engineering mode. The Decision Call surfaces which mode fits.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {DELIVERY_MODES.map(({ icon: Icon, badge, title, blurb, bestFor, bestForItems }) => (
                <Card key={title} className="p-7 bg-white border border-gray-200 shadow-sm flex flex-col">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-accent" aria-hidden />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1">
                        {badge}
                      </p>
                      <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-5">{blurb}</p>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">
                      {bestFor}
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      {bestForItems.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-accent mt-1.5 flex-shrink-0">
                            <span className="block w-1.5 h-1.5 rounded-full bg-accent" />
                          </span>
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Four engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same four tiers cover upgrades, OCI lift-and-shift, and modernization. The tier
              defines the shape of the engagement; the delivery mode (Standard or Forward Deployed)
              defines how the work is staffed and priced.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.tier} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-accent">
                      {t.tier} · {t.label}
                    </p>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                      {t.mode}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* What we read directly */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The founder differentiator
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What we read directly off the implementation
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              No original implementer required. No SME interviews required. No extensive customer
              documentation prerequisite. The running implementation is the source of truth, and we
              read it.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {READ_DIRECTLY.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-forms-recognition-experts"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the Forms Recognition experts page
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Forward paths */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we engage on
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The forward paths we work on
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four published forward paths from the December 2026 cliff. The consulting practice
              engages on any of them — the right path depends on stack complexity, AP team
              requirements, and the customer&rsquo;s strategic frame.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {FORWARD_PATHS.map((p) => (
                <Card key={p.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {p.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{p.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">{p.body}</p>
                  <Link
                    to={p.link}
                    className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors text-sm"
                  >
                    Read more
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </Link>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the December 2026 decision frame
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Verticals */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Verticals
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where the practice engages
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Specific industry frames shape the AP path. Different posting rules, different
              compliance overlays, different controls expectations. The consulting practice carries
              vertical context into every engagement.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {VERTICALS.map(({ icon: Icon, name, body, link }) => (
                <Card key={name} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{name}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">{body}</p>
                      <Link
                        to={link}
                        className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors text-sm"
                      >
                        Industry detail
                        <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement comparison table */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Mode comparison
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Standard Consulting vs Forward Deployed Engineering
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Both modes draw on the same founder-led WebCenter depth. The difference is in how the
              work is shaped, priced, and staffed.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Standard Consulting
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Forward Deployed Engineering
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {ENGAGEMENT_COMPARISON.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.standard}</td>
                      <td className="px-5 py-4 text-gray-700">{row.fde}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* What stays in your control */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Control and partnership
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What stays in your control
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The consulting practice is additive. We work alongside, not in place of, your
              existing Oracle partner relationships where relevant.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {CONTROL_ITEMS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-link banner */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, with stack-specific considerations,
              eight diagnostic questions, and a vendor evaluation checklist. The same frame Andrew
              walks customers through on the Decision Call.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the WebCenter consulting practice
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="text-center mt-10">
              <Link
                to="/about"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                More on Andrew&rsquo;s WebCenter background
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your WebCenter decision
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. 20+ years specifically inside Content (UCM/Stellent),
              Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA Suite, WebLogic, and ADF.
              Walk through your stack and the engagement options in the context of your specific
              implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('modernization-services-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterModernizationServices;
